import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { PickSession } from "@redotech/redo-model/wms/schema/pick-session";
import { createContext, memo, ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavigateToScanStart } from "../common";
import { ClosedFulfillmentGroupModal } from "../modals/closed-fulfillment-group-modal";
import { DuplicateBarcodeModal } from "../modals/duplicate-barcode-modal";
import { IncompleteVerificationModal } from "../modals/incomplete-verification-modal";
import { LeaveSingleItemPickSessionModal } from "../modals/leave-single-item-pick-session-modal";
import { MergedPackingSlipScannedModal } from "../modals/merged-packing-slip-scanned-modal";
import { MultiToteVerificationModal } from "../modals/multi-tote-verification-modal";
import { FulfillmentGroupSelectModal } from "../modals/multiple-order-select-modal";
import {
  AttributesForSplitShipmentModal,
  AttributesForSplitShipmentModalData,
} from "../modals/partially-fulfill-modal";
import { ProblemOrderWarningModal } from "../modals/problem-order-warning-modal";
import { ToteOrderSelectionModal } from "../modals/tote-order-selection-modal";
import { WrongBarcodeModal } from "../modals/wrong-barcode-modal";
import {
  MultiToteVerificationData,
  ScanningModal,
} from "../scanning-context/types";
import { PickSessionContext } from "./pick-session-context";

type ModalState =
  | {
      type: ScanningModal.FULFILLMENT_GROUP_SELECTION;
      data: {
        fulfillmentGroups: FulfillmentGroupData[];
        onSelect: (group: FulfillmentGroupData) => void;
      };
    }
  | {
      type: ScanningModal.INCOMPLETE_VERIFICATION;
      data: { fulfillmentGroupId: string; scanOnLoad?: string };
    }
  | { type: ScanningModal.WRONG_BARCODE; data: string }
  | { type: ScanningModal.DUPLICATE_BARCODE; data: string }
  | {
      type: ScanningModal.MERGED_PACKING_SLIP_SCANNED;
      data: FulfillmentGroupData;
    }
  | {
      type: ScanningModal.MULTI_TOTE_VERIFICATION;
      data: MultiToteVerificationData;
    }
  | {
      type: ScanningModal.TOTE_ORDER_SELECTION;
      data: { onScan: (barcode: string) => void; pending: boolean };
    }
  | { type: ScanningModal.CLOSED_GROUP_WARNING }
  | {
      type: ScanningModal.PARTIAL_FULFILL;
      data: { onSubmit: (data: AttributesForSplitShipmentModalData) => void };
    }
  | {
      type: ScanningModal.LEAVE_SINGLE_ITEM_PICK_SESSION;
      data: { pickSession: PickSession; onContinue: () => void };
    }
  | {
      type: ScanningModal.PROBLEM_ORDER_WARNING;
      data: { fulfillmentGroupId: string };
    }
  | null;

export interface ScanModalContextValue {
  openModalState: ScanningModal | null;
  openModal: (modalState: ModalState) => void;
  closeModal: () => void;
}

export const ScanModalContext = createContext<
  ScanModalContextValue | undefined
>(undefined);

export const ModalProvider = memo(function ModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [modalState, setModalState] = useState<ModalState>(null);
  const navigate = useNavigate();
  const { clearPickSession } = useRequiredContext(PickSessionContext);
  const navigateToScanStart = useNavigateToScanStart();

  const openModal = useHandler((modalState: ModalState) => {
    setModalState(modalState);
  });

  const closeModal = useHandler(() => {
    setModalState(null);
  });

  const renderModal = () => {
    if (!modalState) return null;

    switch (modalState.type) {
      case ScanningModal.FULFILLMENT_GROUP_SELECTION:
        return (
          <FulfillmentGroupSelectModal
            identifier={modalState.data.fulfillmentGroups
              .flatMap((g) => g.orders.map((o) => o.name))
              .join(", ")}
            onClose={closeModal}
            onSelect={modalState.data.onSelect}
            open
            relevantFulfillmentGroups={modalState.data.fulfillmentGroups}
          />
        );

      case ScanningModal.INCOMPLETE_VERIFICATION:
        return (
          <IncompleteVerificationModal
            onBack={closeModal}
            onContinue={() => {
              closeModal();
              const { fulfillmentGroupId, scanOnLoad } = modalState.data;
              if (scanOnLoad) {
                navigate(
                  `${fulfillmentGroupId}?scanOnLoad=${encodeURIComponent(scanOnLoad)}`,
                );
              } else {
                navigate(fulfillmentGroupId);
              }
            }}
            open
          />
        );

      case ScanningModal.WRONG_BARCODE:
        return (
          <WrongBarcodeModal barcode={modalState.data} onClose={closeModal} />
        );

      case ScanningModal.DUPLICATE_BARCODE:
        return (
          <DuplicateBarcodeModal
            barcode={modalState.data}
            onClose={closeModal}
          />
        );

      case ScanningModal.MERGED_PACKING_SLIP_SCANNED:
        return (
          <MergedPackingSlipScannedModal
            fulfillmentGroupData={modalState.data}
            onCancel={() => {
              closeModal();
              navigateToScanStart();
            }}
            onContinue={() => {
              closeModal();
              navigate(modalState.data._id);
            }}
            open
          />
        );

      case ScanningModal.MULTI_TOTE_VERIFICATION:
        return (
          <MultiToteVerificationModal
            data={modalState.data}
            isOpen
            onClose={closeModal}
            onContinue={() => {
              closeModal();
              navigate(modalState.data.fulfillmentGroupId);
            }}
          />
        );

      case ScanningModal.TOTE_ORDER_SELECTION:
        return (
          <ToteOrderSelectionModal
            isOpen
            onClose={() => {
              closeModal();
              clearPickSession();
            }}
            onScan={modalState.data.onScan}
            pending={modalState.data.pending}
          />
        );

      case ScanningModal.CLOSED_GROUP_WARNING:
        return (
          <ClosedFulfillmentGroupModal
            onBack={() => {
              closeModal();
              navigate("../");
            }}
            onContinue={closeModal}
            open
          />
        );

      case ScanningModal.PARTIAL_FULFILL:
        return (
          <AttributesForSplitShipmentModal
            onCloseRequested={closeModal}
            onSubmit={modalState.data.onSubmit}
          />
        );

      case ScanningModal.LEAVE_SINGLE_ITEM_PICK_SESSION:
        return (
          <LeaveSingleItemPickSessionModal
            isOpen
            onClose={closeModal}
            onContinue={() => {
              closeModal();
              modalState.data.onContinue();
            }}
            pickSession={modalState.data.pickSession}
          />
        );

      case ScanningModal.PROBLEM_ORDER_WARNING:
        return (
          <ProblemOrderWarningModal
            fulfillmentGroupId={modalState.data.fulfillmentGroupId}
            isOpen
            onContinue={closeModal}
            onGoToProblemOrders={(fulfillmentGroupId: string) => {
              closeModal();
              navigate(`../pick-sessions/problem-order/${fulfillmentGroupId}`);
            }}
            onScanNext={() => {
              closeModal();
              navigateToScanStart();
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <ScanModalContext.Provider
      value={{
        openModalState: modalState?.type ?? null,
        openModal,
        closeModal,
      }}
    >
      {children}
      {renderModal()}
    </ScanModalContext.Provider>
  );
});
